import { useId } from 'react';
import { T, useGT } from 'gt-react';
import { ArrowUUpLeftIcon } from '@phosphor-icons/react';
import { useRemoveForwardBuy } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { toast } from 'src/components/ui/toast';

interface ForwardBuyRemoveActionProps {
  supplierId: string;
  shipToWarehouseId: string;
  /** Names the cart when the vendor has more than one ship-to on the board. */
  shipToWarehouseName?: string | null;
}

/**
 * The one buyer action on forward buying (docs/procurement/forward-buying.md,
 * "Board surfaces"): take the extra quantities back out of one vendor cart.
 *
 * It lives in the supplier's detail view, under the cart's MOV chip, where the
 * buyer sees the lines it changes. The belt card only summarizes. The removal
 * restores the baseline choices and stores no preference, which is why the
 * copy says the next run may add them again.
 */
export function ForwardBuyRemoveAction({
  supplierId,
  shipToWarehouseId,
  shipToWarehouseName,
}: ForwardBuyRemoveActionProps) {
  const gt = useGT();
  const remove = useRemoveForwardBuy();
  const helpId = useId();

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded border border-amber-200 bg-amber-50/60 px-2 py-1 text-[11px] leading-snug text-amber-800">
      {shipToWarehouseName != null && (
        <span className="font-medium">{shipToWarehouseName}</span>
      )}
      <button
        type="button"
        disabled={remove.isPending}
        aria-describedby={helpId}
        onClick={() =>
          remove.mutate(
            { supplierId, shipToWarehouseId },
            {
              onError: () => {
                toast.error(
                  gt('Could not remove the forward buy. Try again.', {
                    context: 'replenishment forward-buy removal error toast',
                  }),
                );
              },
            },
          )
        }
        className="inline-flex items-center gap-1 rounded px-1 py-0.5 font-medium underline-offset-2 hover:bg-black/5 hover:underline disabled:opacity-50"
      >
        <ArrowUUpLeftIcon className="h-3 w-3" />
        <T context="replenishment cart button: take the extra quantities forward buying added back out of this vendor cart">
          Remove Forward Buy
        </T>
      </button>
      <span id={helpId} className="opacity-80">
        <T context="replenishment cart helper text under the Remove Forward Buy button: what the removal does and that it is not permanent">
          Removes the extra quantities from this cart. The next replenishment
          run may add them again.
        </T>
      </span>
    </div>
  );
}
